import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { fetchEvaluationMethods } from '../features/risks/risksApi';
import { fetchOrgUnitTree } from '../features/orgUnits/orgUnitsApi';
import type { OrgUnitNode } from '../features/orgUnits/types';
import {
  createRiskStudy,
  duplicateRiskStudy,
  fetchRiskStudies,
} from '../features/riskStudies/riskStudiesApi';
import { STUDY_STATUS_LABEL } from '../features/riskStudies/types';

function flattenOrgUnits(nodes: OrgUnitNode[]): OrgUnitNode[] {
  const out: OrgUnitNode[] = [];
  const walk = (list: OrgUnitNode[]) => {
    for (const n of list) {
      out.push(n);
      if (n.children.length) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

export function RiskStudiesPage() {
  const { canEdit } = usePlatformAccessLevel('risk_studies.access');
  const [studies, setStudies] = useState<Awaited<ReturnType<typeof fetchRiskStudies>>>([]);
  const [orgUnits, setOrgUnits] = useState<OrgUnitNode[]>([]);
  const [methods, setMethods] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: '',
    title: '',
    orgUnitId: '',
    evaluationMethodId: '',
    studyDate: new Date().toISOString().slice(0, 10),
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, tree, m] = await Promise.all([
        fetchRiskStudies(),
        fetchOrgUnitTree(),
        fetchEvaluationMethods(),
      ]);
      setStudies(s);
      setOrgUnits(flattenOrgUnits(tree));
      setMethods(m);
      if (m[0] && !form.evaluationMethodId) {
        setForm(f => ({ ...f, evaluationMethodId: m[0].id }));
      }
    } finally {
      setLoading(false);
    }
  }, [form.evaluationMethodId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    await createRiskStudy({ ...form, studyDate: new Date(form.studyDate).toISOString() });
    setShowForm(false);
    await load();
  };

  const handleDuplicate = async (id: string) => {
    await duplicateRiskStudy(id);
    await load();
  };

  return (
    <div className='mx-auto max-w-6xl p-6'>
      <div className='mb-6 flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold'>Estudios IPER / AMFE</h1>
          <p className='text-sm text-muted-foreground'>Una hoja FQ-22 = un estudio por proceso</p>
        </div>
        {canEdit && (
          <button
            type='button'
            className='rounded bg-blue-600 px-4 py-2 text-white'
            onClick={() => setShowForm(!showForm)}
          >
            Nuevo estudio
          </button>
        )}
      </div>

      {showForm && canEdit && (
        <form onSubmit={handleCreate} className='mb-6 grid gap-3 rounded border border-border bg-card p-4 md:grid-cols-2'>
          <input
            className='rounded border px-3 py-2'
            placeholder='Código'
            value={form.code}
            onChange={e => setForm({ ...form, code: e.target.value })}
            required
          />
          <input
            className='rounded border px-3 py-2'
            placeholder='Título'
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            required
          />
          <select
            className='rounded border px-3 py-2'
            value={form.orgUnitId}
            onChange={e => setForm({ ...form, orgUnitId: e.target.value })}
            required
          >
            <option value=''>Nodo / proceso</option>
            {orgUnits.map(u => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.kind})
              </option>
            ))}
          </select>
          <select
            className='rounded border px-3 py-2'
            value={form.evaluationMethodId}
            onChange={e => setForm({ ...form, evaluationMethodId: e.target.value })}
            required
          >
            {methods.map(m => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <input
            type='date'
            className='rounded border px-3 py-2'
            value={form.studyDate}
            onChange={e => setForm({ ...form, studyDate: e.target.value })}
            required
          />
          <button type='submit' className='rounded bg-green-600 px-4 py-2 text-white'>
            Crear
          </button>
        </form>
      )}

      {loading ? (
        <p>Cargando…</p>
      ) : (
        <table className='min-w-full rounded border border-border text-sm'>
          <thead className='bg-muted/50 text-foreground'>
            <tr>
              <th className='px-3 py-2 text-left'>Código</th>
              <th className='px-3 py-2 text-left'>Título</th>
              <th className='px-3 py-2 text-left'>Ubicación</th>
              <th className='px-3 py-2 text-left'>Estado</th>
              <th className='px-3 py-2 text-left'>Actividades</th>
              <th className='px-3 py-2 text-left'>Riesgos</th>
              <th className='px-3 py-2'>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {studies.map(study => (
              <tr key={study.id} className='border-t border-border'>
                <td className='px-3 py-2 font-mono'>{study.code}</td>
                <td className='px-3 py-2'>{study.title}</td>
                <td className='px-3 py-2'>{study.orgUnit?.name ?? '—'}</td>
                <td className='px-3 py-2'>{STUDY_STATUS_LABEL[study.status]}</td>
                <td className='px-3 py-2'>{study._count?.activities ?? 0}</td>
                <td className='px-3 py-2'>{study._count?.risks ?? 0}</td>
                <td className='px-3 py-2'>
                  <Link to={`/estudios-riesgo/${study.id}`} className='mr-2 text-primary'>
                    Abrir
                  </Link>
                  {canEdit && (
                    <button
                      type='button'
                      className='text-muted-foreground hover:text-foreground'
                      onClick={() => void handleDuplicate(study.id)}
                    >
                      Duplicar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
